import { useState, useEffect, useCallback, useMemo } from 'react';
import AnimatedPage from '@/components/AnimatedPage';
import {
  GlassCard,
  GlassCardContent,
  GlassCardDescription,
  GlassCardHeader,
  GlassCardTitle,
} from '@easyflow/ui-master/components/ui/glass-card';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Input } from '@easyflow/ui-master/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@easyflow/ui-master/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@easyflow/ui-master/components/ui/table';
import { SkeletonTable } from '@easyflow/ui-master/components/ui/skeleton-loader';
import {
  Tags,
  Plus,
  Pencil,
  Trash2,
  RefreshCw,
  Save,
  X,
  Power,
  Network,
  Search,
  ChevronRight,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import apiClient from '@/lib/api';
import { toErr, type RetailBrandPaymentMode } from '@easyflow/shared';
import PaymentModesSection from './retail-brands/PaymentModesSection';

interface ChannelRow {
  id: string;
  brand_id: string;
  code: string;
  display_name: string;
  is_active: boolean;
  sort_order: number;
}

interface BrandWithChannels {
  id: string;
  code: string;
  display_name: string;
  logo_url: string | null;
  is_active: boolean;
  channels: ChannelRow[];
}

interface ChannelDraft {
  display_name: string;
  code: string;
  sort_order: string;
}

interface BrandDraft {
  display_name: string;
  code: string;
  logoFile: File | null;
}

interface ViewerPermissions {
  can_create_brand: boolean;
  can_update_brand: boolean;
  can_manage_channels: boolean;
}

interface BrandsListResponse {
  brands: BrandWithChannels[];
  viewer_permissions: ViewerPermissions;
}

const noPermissions: ViewerPermissions = {
  can_create_brand: false,
  can_update_brand: false,
  can_manage_channels: false,
};

type ActiveFilter = 'all' | 'active' | 'inactive';

const emptyDraft: ChannelDraft = { display_name: '', code: '', sort_order: '0' };
const emptyBrandDraft: BrandDraft = { display_name: '', code: '', logoFile: null };

const LOGO_ACCEPT = 'image/png,image/jpeg,image/webp,image/svg+xml';
const LOGO_MAX_BYTES = 2 * 1024 * 1024;

export default function RetailBrandChannelsMaster() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [brands, setBrands] = useState<BrandWithChannels[]>([]);
  // Permessi del viewer (dal backend): gating dei bottoni CRUD.
  const [perms, setPerms] = useState<ViewerPermissions>(noPermissions);
  // Insegne espanse (chiuse di default).
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  // Ricerca + filtri.
  const [search, setSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState<ActiveFilter>('all');
  const [channelFilter, setChannelFilter] = useState<ActiveFilter>('all');
  // Creazione insegna.
  const [creatingBrand, setCreatingBrand] = useState(false);
  const [brandDraft, setBrandDraft] = useState<BrandDraft>(emptyBrandDraft);
  // Modifica insegna (inline nell'header della card).
  const [editingBrandId, setEditingBrandId] = useState<string | null>(null);
  const [brandEditDraft, setBrandEditDraft] = useState<BrandDraft>(emptyBrandDraft);
  const [brandEditRemoveLogo, setBrandEditRemoveLogo] = useState(false);
  // brandId in fase di creazione → draft del nuovo canale
  const [creatingFor, setCreatingFor] = useState<string | null>(null);
  const [createDraft, setCreateDraft] = useState<ChannelDraft>(emptyDraft);
  // channelId in modifica → draft
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<ChannelDraft>(emptyDraft);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [paymentModes, setPaymentModes] = useState<RetailBrandPaymentMode[]>([]);
  const [canManagePaymentModes, setCanManagePaymentModes] = useState(false);
  const [paymentModesLoading, setPaymentModesLoading] = useState(true);
  const [paymentModesError, setPaymentModesError] = useState<string | null>(null);

  const loadPaymentModes = useCallback(async (): Promise<void> => {
    setPaymentModesLoading(true);
    setPaymentModesError(null);
    try {
      const res = await apiClient.get<{
        modes: RetailBrandPaymentMode[];
        viewer_permissions: { can_manage_payment_modes: boolean };
      }>('/master/retail-brand-payment-modes?scope=all&include_inactive=true');
      if (!res.success || res.data === undefined) {
        throw new Error(res.error ?? res.message ?? 'Impossibile caricare le modalità di pagamento');
      }
      setPaymentModes(res.data.modes);
      setCanManagePaymentModes(res.data.viewer_permissions.can_manage_payment_modes);
    } catch (err) {
      setCanManagePaymentModes(false);
      setPaymentModesError(toErr(err).message);
    } finally {
      setPaymentModesLoading(false);
    }
  }, []);

  const paymentModesByBrand = useMemo(() => {
    const grouped = new Map<string, RetailBrandPaymentMode[]>();
    for (const mode of paymentModes) {
      const group = grouped.get(mode.brand_id) ?? [];
      group.push(mode);
      grouped.set(mode.brand_id, group);
    }
    for (const group of grouped.values()) {
      group.sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
    }
    return grouped;
  }, [paymentModes]);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [res] = await Promise.all([
        apiClient.get<BrandsListResponse>('/master/retail-brand-channels'),
        loadPaymentModes(),
      ]);
      setBrands(res.data?.brands ?? []);
      setPerms(res.data?.viewer_permissions ?? noPermissions);
    } catch (err) {
      toast({ title: 'Errore', description: toErr(err).message, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [toast, loadPaymentModes]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleExpanded = (brandId: string): void => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(brandId)) next.delete(brandId);
      else next.add(brandId);
      return next;
    });
  };

  // Anteprime logo (object URL) con revoca al cambio file/unmount.
  const createLogoPreview = useMemo(
    () => (brandDraft.logoFile !== null ? URL.createObjectURL(brandDraft.logoFile) : null),
    [brandDraft.logoFile],
  );
  useEffect(() => () => { if (createLogoPreview !== null) URL.revokeObjectURL(createLogoPreview); }, [createLogoPreview]);
  const editLogoPreview = useMemo(
    () => (brandEditDraft.logoFile !== null ? URL.createObjectURL(brandEditDraft.logoFile) : null),
    [brandEditDraft.logoFile],
  );
  useEffect(() => () => { if (editLogoPreview !== null) URL.revokeObjectURL(editLogoPreview); }, [editLogoPreview]);

  const validateLogoFile = (file: File): boolean => {
    if (!LOGO_ACCEPT.split(',').includes(file.type)) {
      toast({ title: 'Formato non supportato', description: 'Usa PNG, JPEG, WebP o SVG', variant: 'destructive' });
      return false;
    }
    if (file.size > LOGO_MAX_BYTES) {
      toast({ title: 'File troppo grande', description: 'Dimensione massima 2 MB', variant: 'destructive' });
      return false;
    }
    return true;
  };

  const uploadBrandLogo = async (brandId: string, file: File): Promise<void> => {
    const form = new FormData();
    form.append('file', file);
    await apiClient.post(`/master/retail-brand-channels/brands/${brandId}/logo`, form);
  };

  const submitCreateBrand = async (): Promise<void> => {
    if (brandDraft.display_name.trim() === '') {
      toast({ title: 'Nome obbligatorio', description: "Inserisci il nome dell'insegna", variant: 'destructive' });
      return;
    }
    setBusyId('__new_brand__');
    try {
      const res = await apiClient.post<BrandWithChannels>('/master/retail-brand-channels/brands', {
        display_name: brandDraft.display_name.trim(),
        code: brandDraft.code.trim() !== '' ? brandDraft.code.trim() : undefined,
      });
      const createdId = res.data?.id;
      if (createdId !== undefined && brandDraft.logoFile !== null) {
        try {
          await uploadBrandLogo(createdId, brandDraft.logoFile);
        } catch (logoErr) {
          toast({ title: 'Insegna creata, logo non caricato', description: toErr(logoErr).message, variant: 'destructive' });
        }
      }
      toast({ title: 'Insegna creata', description: brandDraft.display_name.trim() });
      setCreatingBrand(false);
      setBrandDraft(emptyBrandDraft);
      if (createdId !== undefined) {
        setExpandedIds((prev) => new Set(prev).add(createdId));
      }
      await load();
    } catch (err) {
      toast({ title: 'Errore', description: toErr(err).message, variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const startEditBrand = (brand: BrandWithChannels): void => {
    setCreatingFor(null);
    setEditingId(null);
    setEditingBrandId(brand.id);
    setBrandEditDraft({ display_name: brand.display_name, code: brand.code, logoFile: null });
    setBrandEditRemoveLogo(false);
  };

  const submitEditBrand = async (brand: BrandWithChannels): Promise<void> => {
    if (brandEditDraft.display_name.trim() === '') {
      toast({ title: 'Nome obbligatorio', description: "Inserisci il nome dell'insegna", variant: 'destructive' });
      return;
    }
    setBusyId(brand.id);
    try {
      await apiClient.patch(`/master/retail-brand-channels/brands/${brand.id}`, {
        display_name: brandEditDraft.display_name.trim(),
        code: brandEditDraft.code.trim() !== '' ? brandEditDraft.code.trim() : undefined,
        ...(brandEditRemoveLogo && brandEditDraft.logoFile === null ? { logo_url: '' } : {}),
      });
      if (brandEditDraft.logoFile !== null) {
        await uploadBrandLogo(brand.id, brandEditDraft.logoFile);
      }
      toast({ title: 'Insegna aggiornata', description: brandEditDraft.display_name.trim() });
      setEditingBrandId(null);
      setBrandEditDraft(emptyBrandDraft);
      setBrandEditRemoveLogo(false);
      await load();
    } catch (err) {
      toast({ title: 'Errore', description: toErr(err).message, variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const toggleBrandActive = async (brand: BrandWithChannels): Promise<void> => {
    setBusyId(brand.id);
    try {
      await apiClient.patch(`/master/retail-brand-channels/brands/${brand.id}`, {
        is_active: !brand.is_active,
      });
      toast({ title: brand.is_active ? 'Insegna disattivata' : 'Insegna attivata', description: brand.display_name });
      await load();
    } catch (err) {
      toast({ title: 'Errore', description: toErr(err).message, variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const startCreate = (brandId: string): void => {
    setEditingId(null);
    setCreatingFor(brandId);
    setCreateDraft(emptyDraft);
    setExpandedIds((prev) => new Set(prev).add(brandId));
  };

  const submitCreate = async (brandId: string): Promise<void> => {
    if (createDraft.display_name.trim() === '') {
      toast({ title: 'Nome obbligatorio', description: 'Inserisci il nome del canale', variant: 'destructive' });
      return;
    }
    setBusyId(brandId);
    try {
      const sort = Number.parseInt(createDraft.sort_order, 10);
      await apiClient.post(`/master/retail-brand-channels/brands/${brandId}/channels`, {
        display_name: createDraft.display_name.trim(),
        code: createDraft.code.trim() !== '' ? createDraft.code.trim() : undefined,
        sort_order: Number.isFinite(sort) ? sort : 0,
      });
      toast({ title: 'Canale creato', description: createDraft.display_name.trim() });
      setCreatingFor(null);
      setCreateDraft(emptyDraft);
      await load();
    } catch (err) {
      toast({ title: 'Errore', description: toErr(err).message, variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const startEdit = (ch: ChannelRow): void => {
    setCreatingFor(null);
    setEditingId(ch.id);
    setEditDraft({ display_name: ch.display_name, code: ch.code, sort_order: String(ch.sort_order) });
  };

  const submitEdit = async (channelId: string): Promise<void> => {
    if (editDraft.display_name.trim() === '') {
      toast({ title: 'Nome obbligatorio', description: 'Inserisci il nome del canale', variant: 'destructive' });
      return;
    }
    setBusyId(channelId);
    try {
      const sort = Number.parseInt(editDraft.sort_order, 10);
      await apiClient.patch(`/master/retail-brand-channels/channels/${channelId}`, {
        display_name: editDraft.display_name.trim(),
        code: editDraft.code.trim() !== '' ? editDraft.code.trim() : undefined,
        sort_order: Number.isFinite(sort) ? sort : 0,
      });
      toast({ title: 'Canale aggiornato' });
      setEditingId(null);
      await load();
    } catch (err) {
      toast({ title: 'Errore', description: toErr(err).message, variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const toggleActive = async (ch: ChannelRow): Promise<void> => {
    setBusyId(ch.id);
    try {
      await apiClient.patch(`/master/retail-brand-channels/channels/${ch.id}`, {
        is_active: !ch.is_active,
      });
      toast({ title: ch.is_active ? 'Canale disattivato' : 'Canale attivato', description: ch.display_name });
      await load();
    } catch (err) {
      toast({ title: 'Errore', description: toErr(err).message, variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (ch: ChannelRow): Promise<void> => {
    if (!window.confirm(`Eliminare il canale "${ch.display_name}"? L'operazione è definitiva.`)) return;
    setBusyId(ch.id);
    try {
      await apiClient.delete(`/master/retail-brand-channels/channels/${ch.id}`);
      toast({ title: 'Canale eliminato', description: ch.display_name });
      await load();
    } catch (err) {
      toast({ title: 'Impossibile eliminare', description: toErr(err).message, variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const query = search.trim().toLowerCase();
  const hasQuery = query !== '';

  const filteredBrands = useMemo(() => {
    const paymentModeMatches = (brandId: string): boolean =>
      (paymentModesByBrand.get(brandId) ?? []).some(
        (mode) => mode.name.toLowerCase().includes(query) || mode.code.toLowerCase().includes(query),
      );
    return brands
      .filter((b) => {
        if (brandFilter === 'active' && !b.is_active) return false;
        if (brandFilter === 'inactive' && b.is_active) return false;
        return true;
      })
      .map((b) => {
        const channels = b.channels.filter((ch) => {
          if (channelFilter === 'active' && !ch.is_active) return false;
          if (channelFilter === 'inactive' && ch.is_active) return false;
          return true;
        });
        return { ...b, channels };
      })
      .filter((b) => {
        if (!hasQuery) return true;
        const brandMatch = b.display_name.toLowerCase().includes(query)
          || b.code.toLowerCase().includes(query);
        if (brandMatch || paymentModeMatches(b.id)) return true;
        return b.channels.some(
          (ch) => ch.display_name.toLowerCase().includes(query) || ch.code.toLowerCase().includes(query),
        );
      })
      .map((b) => {
        // Se la ricerca matcha solo alcuni canali (e non l'insegna), mostra solo quelli.
        if (!hasQuery) return b;
        const brandMatch = b.display_name.toLowerCase().includes(query)
          || b.code.toLowerCase().includes(query);
        if (brandMatch || paymentModeMatches(b.id)) return b;
        return {
          ...b,
          channels: b.channels.filter(
            (ch) => ch.display_name.toLowerCase().includes(query) || ch.code.toLowerCase().includes(query),
          ),
        };
      });
  }, [brands, brandFilter, channelFilter, query, hasQuery, paymentModesByBrand]);

  const totalChannels = brands.reduce((acc, b) => acc + b.channels.length, 0);
  const hasActiveFilters = hasQuery || brandFilter !== 'all' || channelFilter !== 'all';

  const isExpanded = (brandId: string): boolean =>
    hasQuery || expandedIds.has(brandId) || creatingFor === brandId;

  return (
    <AnimatedPage>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-primary/10 p-2.5 text-primary">
              <Tags className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">Insegne, canali e modalità di pagamento</h1>
              <p className="text-sm text-muted-foreground">
                Cataloghi globali condivisi tra i tenant. Le modalità commerciali del listino sono distinte dai metodi di incasso scelti in cassa.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => void load()} disabled={isLoading || paymentModesLoading}>
              <RefreshCw className={`mr-2 h-4 w-4 ${isLoading || paymentModesLoading ? 'animate-spin' : ''}`} />
              Aggiorna
            </Button>
            {perms.can_create_brand && (
              <Button size="sm" onClick={() => { setCreatingBrand(true); setBrandDraft(emptyBrandDraft); }}>
                <Plus className="mr-2 h-4 w-4" />
                Nuova insegna
              </Button>
            )}
          </div>
        </div>

        {creatingBrand && (
          <GlassCard>
            <GlassCardHeader>
              <GlassCardTitle className="text-base">Nuova insegna globale</GlassCardTitle>
              <GlassCardDescription>
                Verrà creata come preset condiviso, visibile a tutti i tenant.
              </GlassCardDescription>
            </GlassCardHeader>
            <GlassCardContent>
              <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-52 flex-1 space-y-1">
                  <label className="text-xs font-medium text-muted-foreground" htmlFor="new-brand-name">Nome insegna *</label>
                  <Input
                    id="new-brand-name"
                    autoFocus
                    placeholder="Es. Fastweb"
                    value={brandDraft.display_name}
                    onChange={(e) => setBrandDraft((d) => ({ ...d, display_name: e.target.value }))}
                  />
                </div>
                <div className="w-44 space-y-1">
                  <label className="text-xs font-medium text-muted-foreground" htmlFor="new-brand-code">Codice</label>
                  <Input
                    id="new-brand-code"
                    placeholder="auto"
                    value={brandDraft.code}
                    onChange={(e) => setBrandDraft((d) => ({ ...d, code: e.target.value }))}
                  />
                </div>
                <div className="min-w-52 flex-1 space-y-1">
                  <label className="text-xs font-medium text-muted-foreground" htmlFor="new-brand-logo">Logo (PNG, JPEG, WebP, SVG · max 2 MB)</label>
                  <div className="flex items-center gap-2">
                    {createLogoPreview !== null && (
                      <img
                        src={createLogoPreview}
                        alt="Anteprima logo"
                        className="h-9 w-9 rounded border border-border object-contain"
                      />
                    )}
                    <Input
                      id="new-brand-logo"
                      type="file"
                      accept={LOGO_ACCEPT}
                      onChange={(e) => {
                        const file = e.target.files?.[0] ?? null;
                        if (file !== null && !validateLogoFile(file)) { e.target.value = ''; return; }
                        setBrandDraft((d) => ({ ...d, logoFile: file }));
                      }}
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => void submitCreateBrand()}
                    disabled={busyId === '__new_brand__'}
                  >
                    <Save className="mr-2 h-4 w-4" />
                    Crea
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { setCreatingBrand(false); setBrandDraft(emptyBrandDraft); }}
                  >
                    <X className="mr-2 h-4 w-4" />
                    Annulla
                  </Button>
                </div>
              </div>
            </GlassCardContent>
          </GlassCard>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-64 flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Cerca insegna, canale o modalità dedicata…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={brandFilter} onValueChange={(v) => setBrandFilter(v as ActiveFilter)}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Stato insegna" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Insegne: tutte</SelectItem>
              <SelectItem value="active">Insegne: attive</SelectItem>
              <SelectItem value="inactive">Insegne: inattive</SelectItem>
            </SelectContent>
          </Select>
          <Select value={channelFilter} onValueChange={(v) => setChannelFilter(v as ActiveFilter)}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Stato canali" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Canali: tutti</SelectItem>
              <SelectItem value="active">Canali: attivi</SelectItem>
              <SelectItem value="inactive">Canali: inattivi</SelectItem>
            </SelectContent>
          </Select>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setSearch(''); setBrandFilter('all'); setChannelFilter('all'); }}
            >
              <X className="mr-2 h-4 w-4" />
              Azzera filtri
            </Button>
          )}
        </div>

        {isLoading ? (
          <GlassCard>
            <GlassCardContent className="pt-6">
              <SkeletonTable rows={5} />
            </GlassCardContent>
          </GlassCard>
        ) : brands.length === 0 ? (
          <GlassCard>
            <GlassCardContent className="flex flex-col items-center gap-2 py-16 text-center">
              <Network className="h-10 w-10 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Nessuna insegna globale. Creane una con "Nuova insegna" per poterne gestire i canali.
              </p>
            </GlassCardContent>
          </GlassCard>
        ) : filteredBrands.length === 0 ? (
          <GlassCard>
            <GlassCardContent className="flex flex-col items-center gap-2 py-16 text-center">
              <Search className="h-10 w-10 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Nessun risultato con i filtri correnti.
              </p>
            </GlassCardContent>
          </GlassCard>
        ) : (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">
              {filteredBrands.length} di {brands.length} insegne · {totalChannels} canali totali
            </p>
            {filteredBrands.map((brand) => {
              const open = isExpanded(brand.id);
              return (
                <GlassCard key={brand.id}>
                  <GlassCardHeader
                    className="cursor-pointer select-none"
                    onClick={() => toggleExpanded(brand.id)}
                    role="button"
                    tabIndex={0}
                    aria-expanded={open}
                    onKeyDown={(e) => {
                      if (e.target !== e.currentTarget) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleExpanded(brand.id);
                      }
                    }}
                  >
                    {editingBrandId === brand.id ? (
                      <div
                        className="flex flex-wrap items-end gap-3"
                        role="presentation"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <div className="min-w-48 flex-1 space-y-1">
                          <label className="text-xs font-medium text-muted-foreground" htmlFor={`edit-brand-name-${brand.id}`}>Nome insegna *</label>
                          <Input
                            id={`edit-brand-name-${brand.id}`}
                            autoFocus
                            value={brandEditDraft.display_name}
                            onChange={(e) => setBrandEditDraft((d) => ({ ...d, display_name: e.target.value }))}
                          />
                        </div>
                        <div className="w-40 space-y-1">
                          <label className="text-xs font-medium text-muted-foreground" htmlFor={`edit-brand-code-${brand.id}`}>Codice</label>
                          <Input
                            id={`edit-brand-code-${brand.id}`}
                            value={brandEditDraft.code}
                            onChange={(e) => setBrandEditDraft((d) => ({ ...d, code: e.target.value }))}
                          />
                        </div>
                        <div className="min-w-52 flex-1 space-y-1">
                          <label className="text-xs font-medium text-muted-foreground" htmlFor={`edit-brand-logo-${brand.id}`}>Logo (PNG, JPEG, WebP, SVG · max 2 MB)</label>
                          <div className="flex items-center gap-2">
                            {editLogoPreview !== null ? (
                              <img
                                src={editLogoPreview}
                                alt="Anteprima logo"
                                className="h-9 w-9 rounded border border-border object-contain"
                              />
                            ) : brand.logo_url && !brandEditRemoveLogo ? (
                              <img
                                src={brand.logo_url}
                                alt={brand.display_name}
                                className="h-9 w-9 rounded border border-border object-contain"
                              />
                            ) : null}
                            <Input
                              id={`edit-brand-logo-${brand.id}`}
                              type="file"
                              accept={LOGO_ACCEPT}
                              onChange={(e) => {
                                const file = e.target.files?.[0] ?? null;
                                if (file !== null && !validateLogoFile(file)) { e.target.value = ''; return; }
                                setBrandEditDraft((d) => ({ ...d, logoFile: file }));
                              }}
                            />
                            {brand.logo_url && brandEditDraft.logoFile === null && (
                              <Button
                                size="sm"
                                variant={brandEditRemoveLogo ? 'destructive' : 'outline'}
                                onClick={() => setBrandEditRemoveLogo((v) => !v)}
                                title={brandEditRemoveLogo ? 'Il logo verrà rimosso al salvataggio' : 'Rimuovi logo'}
                              >
                                <Trash2 className="mr-1 h-4 w-4" />
                                {brandEditRemoveLogo ? 'Da rimuovere' : 'Rimuovi'}
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => void submitEditBrand(brand)}
                            disabled={busyId === brand.id}
                          >
                            <Save className="mr-2 h-4 w-4" />
                            Salva
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => { setEditingBrandId(null); setBrandEditDraft(emptyBrandDraft); setBrandEditRemoveLogo(false); }}
                          >
                            <X className="mr-2 h-4 w-4" />
                            Annulla
                          </Button>
                        </div>
                      </div>
                    ) : (
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <ChevronRight
                          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`}
                        />
                        {brand.logo_url ? (
                          <img
                            src={brand.logo_url}
                            alt={brand.display_name}
                            className="h-8 w-8 rounded object-contain"
                          />
                        ) : (
                          <div className="flex h-8 w-8 items-center justify-center rounded bg-muted text-xs font-semibold text-muted-foreground">
                            {brand.display_name.slice(0, 2).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <GlassCardTitle className="text-base">
                            {brand.display_name}
                            {!brand.is_active && (
                              <Badge variant="secondary" className="ml-2 align-middle">inattiva</Badge>
                            )}
                          </GlassCardTitle>
                          <GlassCardDescription className="font-mono text-xs">{brand.code}</GlassCardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground">
                          {brand.channels.length} {brand.channels.length === 1 ? 'canale' : 'canali'}
                          {' · '}{(paymentModesByBrand.get(brand.id) ?? []).length} modalità dedicate
                        </span>
                        {perms.can_update_brand && (
                          <>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); void toggleBrandActive(brand); }}
                              disabled={busyId === brand.id}
                              title={brand.is_active ? 'Disattiva insegna' : 'Attiva insegna'}
                            >
                              <Power className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); startEditBrand(brand); }}
                              disabled={busyId === brand.id}
                              title="Modifica insegna"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {perms.can_manage_channels && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); startCreate(brand.id); }}
                            disabled={busyId === brand.id}
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            Nuovo canale
                          </Button>
                        )}
                      </div>
                    </div>
                    )}
                  </GlassCardHeader>
                  {open && (
                    <GlassCardContent className="space-y-6">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[40%]">Canale</TableHead>
                            <TableHead>Codice</TableHead>
                            <TableHead className="w-20 text-center">Ordine</TableHead>
                            <TableHead className="w-24 text-center">Stato</TableHead>
                            <TableHead className="w-44 text-right">Azioni</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {creatingFor === brand.id && (
                            <TableRow className="bg-muted/40">
                              <TableCell>
                                <Input
                                  autoFocus
                                  placeholder="Nome canale"
                                  value={createDraft.display_name}
                                  onChange={(e) => setCreateDraft((d) => ({ ...d, display_name: e.target.value }))}
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  placeholder="auto"
                                  value={createDraft.code}
                                  onChange={(e) => setCreateDraft((d) => ({ ...d, code: e.target.value }))}
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  value={createDraft.sort_order}
                                  onChange={(e) => setCreateDraft((d) => ({ ...d, sort_order: e.target.value }))}
                                  className="text-center"
                                />
                              </TableCell>
                              <TableCell />
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-1">
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => void submitCreate(brand.id)}
                                    disabled={busyId === brand.id}
                                    title="Salva"
                                  >
                                    <Save className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => { setCreatingFor(null); setCreateDraft(emptyDraft); }}
                                    title="Annulla"
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                          {brand.channels.length === 0 && creatingFor !== brand.id ? (
                            <TableRow>
                              <TableCell colSpan={5} className="py-6 text-center text-sm text-muted-foreground">
                                {hasActiveFilters
                                  ? 'Nessun canale corrispondente ai filtri per questa insegna'
                                  : 'Nessun canale per questa insegna'}
                              </TableCell>
                            </TableRow>
                          ) : (
                            brand.channels.map((ch) => {
                              const isEditing = editingId === ch.id;
                              return (
                                <TableRow key={ch.id} className={ch.is_active ? '' : 'opacity-60'}>
                                  <TableCell>
                                    {isEditing ? (
                                      <Input
                                        autoFocus
                                        value={editDraft.display_name}
                                        onChange={(e) => setEditDraft((d) => ({ ...d, display_name: e.target.value }))}
                                      />
                                    ) : (
                                      <span className="font-medium">{ch.display_name}</span>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {isEditing ? (
                                      <Input
                                        value={editDraft.code}
                                        onChange={(e) => setEditDraft((d) => ({ ...d, code: e.target.value }))}
                                      />
                                    ) : (
                                      <span className="font-mono text-xs text-muted-foreground">{ch.code}</span>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-center">
                                    {isEditing ? (
                                      <Input
                                        type="number"
                                        value={editDraft.sort_order}
                                        onChange={(e) => setEditDraft((d) => ({ ...d, sort_order: e.target.value }))}
                                        className="text-center"
                                      />
                                    ) : (
                                      ch.sort_order
                                    )}
                                  </TableCell>
                                  <TableCell className="text-center">
                                    <Badge variant={ch.is_active ? 'default' : 'secondary'}>
                                      {ch.is_active ? 'attivo' : 'inattivo'}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <div className="flex justify-end gap-1">
                                      {isEditing ? (
                                        <>
                                          <Button
                                            size="icon"
                                            variant="ghost"
                                            onClick={() => void submitEdit(ch.id)}
                                            disabled={busyId === ch.id}
                                            title="Salva"
                                          >
                                            <Save className="h-4 w-4" />
                                          </Button>
                                          <Button
                                            size="icon"
                                            variant="ghost"
                                            onClick={() => setEditingId(null)}
                                            title="Annulla"
                                          >
                                            <X className="h-4 w-4" />
                                          </Button>
                                        </>
                                      ) : perms.can_manage_channels ? (
                                        <>
                                          <Button
                                            size="icon"
                                            variant="ghost"
                                            onClick={() => void toggleActive(ch)}
                                            disabled={busyId === ch.id}
                                            title={ch.is_active ? 'Disattiva' : 'Attiva'}
                                          >
                                            <Power className="h-4 w-4" />
                                          </Button>
                                          <Button
                                            size="icon"
                                            variant="ghost"
                                            onClick={() => startEdit(ch)}
                                            disabled={busyId === ch.id}
                                            title="Modifica"
                                          >
                                            <Pencil className="h-4 w-4" />
                                          </Button>
                                          <Button
                                            size="icon"
                                            variant="ghost"
                                            onClick={() => void remove(ch)}
                                            disabled={busyId === ch.id}
                                            title="Elimina"
                                          >
                                            <Trash2 className="h-4 w-4 text-destructive" />
                                          </Button>
                                        </>
                                      ) : null}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                      <div className="border-t border-border pt-6">
                        <PaymentModesSection
                          brandId={brand.id}
                          modes={paymentModesByBrand.get(brand.id) ?? []}
                          canManage={canManagePaymentModes}
                          isLoading={paymentModesLoading}
                          loadError={paymentModesError}
                          onReload={loadPaymentModes}
                        />
                      </div>
                    </GlassCardContent>
                  )}
                </GlassCard>
              );
            })}
          </div>
        )}
      </div>
    </AnimatedPage>
  );
}
